import { observer } from "mobx-react-lite";
import pageBuilderStore from "../../stores/pageBuilderStore";
import { Brush, CodeXml, Eclipse, Type, ExternalLink } from "lucide-react";
import bodyStylesManager from "../../stores/body-styles-manager";
import contentManager from "../../stores/content-manager";
import serializationManager from "../../stores/serialization-manager";
import SatoriDropDownList from "../ui/shared/satori-ui/dropdownlist";
import pagebuilder from "../pagebuilder";

const BASE_TABS = ["Design", "HTML", "Styles"];
const DISPLAY_TABS = pageBuilderStore.isEmail 
	? [...BASE_TABS, "Text"]
	: BASE_TABS

/**
 * TopBar – Main Header Bar for the Satori PageBuilder Interface
 *
 * @component
 * @description
 * Serves as the fixed top navigation and status bar for the PageBuilder workspace.
 * Includes two rows:
 * - Row 1: Displays the current page/email title, publication status, directory, dev mode selectors,
 *   and context-aware action buttons (e.g., Save, Publish, Open Live Page).
 * - Row 2: Provides navigation tabs for switching between Design, HTML, Styles, and Text modes,
 *   each with relevant icons and an active tab highlight.
 *
 * @features
 * - Dynamic display of publication status (Published/Draft) and folder path
 * - Dropdowns for toggling between dev modes (Editing/Creating) and context (Email/Web Page)
 * - Contextual actions for saving drafts, publishing, saving as template, or opening the live page
 * - Live feedback for auto-saving state
 * - Visual tab navigation with icons, including support for extra tabs in email mode
 *
 * @state
 * - Consumes MobX state from `pageBuilderStore` (title, status, directory, dev mode, tab selection, etc.)
 * - Uses helper stores/managers for serialization and content management actions
 * - Tab selection and action buttons update global builder state
 *
 * @remarks
 * - Not reusable outside PageBuilder; tightly coupled to project MobX state and conventions
 * - Designed for extensibility as new editing modes, tabs, or actions are introduced
 * - Does not accept props; all state flows from global store
 *
 * @example
 * // Include as the main header in PageBuilder layout
 * <TopBar />
 *
 * @since 2025-06-08
 * @lastUpdated 2025-07-15
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default observer(function TopBar () {
  const handlePublish = () => {
    const output = {
      cssContent: [
        ...serializationManager.fromStylesToCssContent(bodyStylesManager.getBodyStylesState()),
        ...serializationManager.serializeAllBlockCssContent(contentManager.getDesignContent())
      ],
      htmlContent: serializationManager.fromBlocksToHTML(contentManager.getDesignContent()),
      jsonContent: serializationManager.fromBlocksToJSON(contentManager.getDesignContent()),
    };

    console.log("📤 Final Output (Publish):", output);

    // Simulate toast
    pageBuilderStore.setToast("Page successfully published!");
    pageBuilderStore.setIsPublished(true);
  };

  const handleSaveAsDraft = () => {
    const output = {
      
      cssContent: [
        ...serializationManager.fromStylesToCssContent(bodyStylesManager.getBodyStylesState()),
        ...serializationManager.serializeAllBlockCssContent(contentManager.getDesignContent())
      ],
      htmlContent: serializationManager.fromBlocksToHTML(contentManager.getDesignContent()),
      jsonContent: serializationManager.fromBlocksToJSON(contentManager.getDesignContent()),
    };

    console.log("💾 Draft Saved:", output);

    pageBuilderStore.setToast("Draft saved.");
  };

  const handleSavePage = () => {
    const cssContentRaw = [
      ...serializationManager.fromStylesToCssContent(bodyStylesManager.getBodyStylesState()),
      ...serializationManager.serializeAllBlockCssContent(contentManager.getDesignContent())
    ];

    console.log("cssContent (raw):", cssContentRaw);
    
    contentManager.setCssContent(contentManager.dedupeCssContent(cssContentRaw));

    contentManager.setHtmlContent(serializationManager.fromBlocksToHTML(contentManager.getDesignContent()));
    contentManager.setJsonContent(serializationManager.fromBlocksToJSON(contentManager.getDesignContent()));

    const output = {
      cssContent: contentManager.dedupeCssContent(cssContentRaw),
      htmlContent: serializationManager.fromBlocksToHTML(contentManager.getDesignContent()),
      jsonContent: serializationManager.fromBlocksToJSON(contentManager.getDesignContent()),
    };

    console.log("💾 Page Saved:", output);


    pageBuilderStore.setToast("Page saved.");
  };

	return (
    <div className="pb-topbar z-20">
      {/* Row 1: Title/Status/Folder (Left), Actions (Right) */}
      <div className="pb-pageinfo">
        {/* Left Side */}
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <span className="font-bold text-lg">{pageBuilderStore.pageTitle}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                pageBuilderStore.isPublished
                  ? "bg-green-100 text-green-700"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {pageBuilderStore.isPublished ? "Published" : "Draft"}
            </span>
          </div>
          <div className="ml-6 flex items-center text-gray-400 text-xs">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 7V6a2 2 0 012-2h2m0 0h10a2 2 0 012 2v1m-14 0v10a2 2 0 002 2h2m0 0h6a2 2 0 002-2V7m-10 0V6"
              ></path>
            </svg>
            {pageBuilderStore.currDir}
          </div>
          <div className="w-[150px] justify-start pl-10 p-2 items-center flex">
            Dev Mode:
          </div>
          <div className="flex items-center w-[120px]">
            <SatoriDropDownList
              options={[{ text: "Creating", value: "creating" }, { text: "Editing", value: "editing"}]}
              value={pageBuilderStore.getIsEditingContent() ? "editing" : "creating"}
              onChange={(value) => {
                pageBuilderStore.setIsEditingContent(value === "editing");
              }}
              isFontFamily={false}
            />
          </div>
          <div className="w-2">

          </div>
          <div className="flex items-center w-[150px]">
            <SatoriDropDownList
              options={[{ text: "Email", value: "email" }, { text: "Web Page", value: "webpage"}]}
              value={pageBuilderStore.getIsEmail() ? "email" : "webpage"}
              onChange={(value) => {
                pageBuilderStore.setIsEmail(value === "email");
              }}
              isFontFamily={false}
            />
          </div>
        </div>
        {/* Right Side */}
        <div className="flex space-x-3">
          {pageBuilderStore.isPublished || pageBuilderStore.getIsEditingContent() ? (
            <>
              {/* Auto-saving status text */}
              {pageBuilderStore.isAutoSaving && (
                <span className="text-sm text-gray-400 mr-4 self-center">
                  Saving Page...
                </span>
              )}
              <button
                onClick={() => window.open("/live/your-page-url", "_blank")}
                className="p-2 rounded hover:bg-gray-100 text-gray-600 mr-4"
                title="Open Live Page"
              >
                <ExternalLink size={20} strokeWidth={1.75} />
              </button>
              <button 
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                onClick={handleSavePage}
                disabled={pageBuilderStore.isAutoSaving}
              >
                Save Page
              </button>
              {/* <button className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 font-medium">
                Save as Template
              </button> */}
            </>
          ) : (
            <>
              {/* Same structure for draft */}
              {pageBuilderStore.isAutoSaving && (
                <span className="text-sm text-gray-400 mr-3 self-center">
                  Saving Draft...
                </span>
              )}

              <button 
                className="px-4 py-2 bg-gray-100 text-black rounded hover:bg-gray-300 font-medium"
                onClick={handleSaveAsDraft}
                disabled={pageBuilderStore.isAutoSaving}
              >
                Save as Draft
              </button>
              <button 
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                onClick={handlePublish}
              >
                Publish
              </button>
            </>
          )}
        </div>
      </div>
      {/* Row 2: Tabs */}
      <div className="flex border-t px-6 h-[36px] items-start">
        {DISPLAY_TABS.map((tab) => (
          <button
            key={tab}
            className={`flex mr-3 px-3  text-sm items-center font-medium rounded-t transition-colors ${
              pageBuilderStore.selectedTab === tab 
                ? "h-[36px] border-blue-400 border-b-[4px] text-blue-600 font-medium bg-white"
                : "h-[34px] hover:bg-gray-100  hover:text-blue-400 bg-white text-gray-500"
            } focus:outline-none`}
            onClick={() => pageBuilderStore.setSelectedTab(tab)}
          >
            {tab === "Design" && (
              <Brush strokeWidth={1.5} size={16} className="mr-2" />
            )}
            {tab === "HTML" && (
              <CodeXml strokeWidth={1.5} size={16} className="mr-2" />
            )}
            {tab === "Styles" && (
              <Eclipse strokeWidth={1.5} size={16} className="mr-2" />
            )}
						{tab === "text" && (
							<Type strokeWidth={1.5} size={16} className="mr-2" />
						)}
            {tab}
          </button>
        ))}
      </div>
    </div>
  );
});
